import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles,
    TreePine, ChevronDown
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIAS_TEMOR = [
    { id: 'abandono', label: 'Abandono', color: 'bg-[#7A9BA8]' },
    { id: 'rechazo', label: 'Rechazo', color: 'bg-[#D07F7E]' },
    { id: 'fracaso', label: 'Fracaso', color: 'bg-[#E8B27A]' },
    { id: 'incompetencia', label: 'Incompetencia', color: 'bg-[#3E5F6E]' },
    { id: 'no_ser_suficiente', label: 'No ser suficiente', color: 'bg-[#9B8BB8]' },
    { id: 'perdida_control', label: 'Pérdida de control', color: 'bg-[#5FAF9D]' },
    { id: 'vulnerabilidad', label: 'Vulnerabilidad', color: 'bg-[#7A9BA8]' },
    { id: 'decepcion', label: 'Decepcionar', color: 'bg-[#D07F7E]' }
];

export default function Modulo24ArbolTemor() {
    const [step, setStep] = useState(1);
    const [siSaleMal, setSiSaleMal] = useState('');
    const [queSignifica, setQueSignifica] = useState('');
    const [queDueleMas, setQueDueleMas] = useState('');
    const [categoriaFinal, setCategoriaFinal] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/arbol_temor`, {
                    withCredentials: true
                });
                if (data.data) {
                    if (data.data.siSaleMal) setSiSaleMal(data.data.siSaleMal);
                    if (data.data.queSignifica) setQueSignifica(data.data.queSignifica);
                    if (data.data.queDueleMas) setQueDueleMas(data.data.queDueleMas);
                    if (data.data.categoriaFinal) setCategoriaFinal(data.data.categoriaFinal);
                    // Set step based on progress
                    if (data.data.queDueleMas) setStep(4);
                    else if (data.data.queSignifica) setStep(3);
                    else if (data.data.siSaleMal) setStep(2);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'arbol_temor', data: { siSaleMal, queSignifica, queDueleMas, categoriaFinal } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [siSaleMal, queSignifica, queDueleMas, categoriaFinal]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [siSaleMal, queSignifica, queDueleMas, categoriaFinal, saveDraft, loadingDraft, readOnly]);

    const canProceed = (currentStep) => {
        switch(currentStep) {
            case 1: return siSaleMal.trim().length > 0;
            case 2: return queSignifica.trim().length > 0;
            case 3: return queDueleMas.trim().length > 0;
            case 4: return categoriaFinal !== '';
            default: return false;
        }
    };

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!categoriaFinal) {
            toast.error('Selecciona una categoría');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/arbol-temor/submit`,
                { 
                    si_sale_mal: siSaleMal,
                    que_significa: queSignifica,
                    que_duele_mas: queDueleMas,
                    categoria_temor_raiz: categoriaFinal
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar');
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        const categoria = CATEGORIAS_TEMOR.find(c => c.id === categoriaFinal);
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className={`w-20 h-20 ${categoria?.color} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                            <TreePine className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Raíz descubierta</h2>
                        <p className={`text-lg font-medium ${categoria?.color.replace('bg-', 'text-')} mb-4`}>
                            {categoria?.label}
                        </p>
                        
                        {/* Chain visualization */}
                        <div className="bg-[#F5F7F8] rounded-xl p-4 text-left mb-6 space-y-2">
                            <div className="flex items-start gap-2">
                                <ChevronDown className="h-4 w-4 text-[#7A8A93] mt-1 shrink-0" />
                                <p className="text-sm text-[#4A5B63]">Si sale mal: <span className="text-[#1F2A30]">{siSaleMal}</span></p>
                            </div>
                            <div className="flex items-start gap-2 pl-4">
                                <ChevronDown className="h-4 w-4 text-[#7A8A93] mt-1 shrink-0" />
                                <p className="text-sm text-[#4A5B63]">Significa: <span className="text-[#1F2A30]">{queSignifica}</span></p>
                            </div>
                            <div className="flex items-start gap-2 pl-8">
                                <ChevronDown className="h-4 w-4 text-[#7A8A93] mt-1 shrink-0" />
                                <p className="text-sm text-[#4A5B63]">Lo que más duele: <span className="text-[#1F2A30]">{queDueleMas}</span></p>
                            </div>
                        </div>
                        
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="mb-6"><ModuleFeedback moduleId="arbol_temor" chapter={2} /></div>
                        <Link to="/usuario">
                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 2</span>
                                    <span className="text-xs text-[#7A8A93]">•</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 2.4</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Árbol de temor raíz</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                            <span className="text-sm text-[#7A8A93]">Paso {step} de 4</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Progress */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <div className="flex gap-2">
                    {[1, 2, 3, 4].map((s) => (
                        <div
                            key={s}
                            className={`flex-1 h-1.5 rounded-full transition-all ${
                                s <= step ? 'bg-[#3E5F6E]' : 'bg-[#DCE3E8]'
                            }`}
                        />
                    ))}
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-8">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <TreePine className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">Baja hasta la raíz</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Explora el temor profundo detrás de tu evitación</p>
                            </div>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="p-8">
                        {/* Step 1 */}
                        {step >= 1 && (
                            <div className={`space-y-4 mb-8 ${step > 1 ? 'opacity-60' : ''}`}>
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        step > 1 ? 'bg-[#5FAF9D] text-white' : 'bg-[#3E5F6E] text-white'
                                    }`}>
                                        {step > 1 ? <Check className="h-4 w-4" /> : '1'}
                                    </div>
                                    <Label className="text-sm font-semibold text-[#3E5F6E]">
                                        Si esta tarea sale mal, ¿qué pasaría?
                                    </Label>
                                </div>
                                <Textarea
                                    value={siSaleMal}
                                    onChange={(e) => setSiSaleMal(e.target.value)}
                                    placeholder="Imagina el peor escenario. ¿Qué consecuencias habría?"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    disabled={step > 1}
                                    data-testid="si-sale-mal-input"
                                />
                                {step === 1 && (
                                    <Button
                                        onClick={() => canProceed(1) && setStep(2)}
                                        disabled={!canProceed(1)}
                                        className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white"
                                    >
                                        Continuar <ChevronDown className="h-4 w-4 ml-1" />
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Step 2 */}
                        {step >= 2 && (
                            <div className={`space-y-4 mb-8 pl-4 border-l-2 border-[#DCE3E8] ${step > 2 ? 'opacity-60' : ''}`}>
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        step > 2 ? 'bg-[#5FAF9D] text-white' : 'bg-[#3E5F6E] text-white'
                                    }`}>
                                        {step > 2 ? <Check className="h-4 w-4" /> : '2'}
                                    </div>
                                    <Label className="text-sm font-semibold text-[#3E5F6E]">
                                        ¿Y eso qué significaría sobre ti?
                                    </Label>
                                </div>
                                <Textarea
                                    value={queSignifica}
                                    onChange={(e) => setQueSignifica(e.target.value)}
                                    placeholder="Si eso pasara, ¿qué diría eso de ti como persona?"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    disabled={step > 2}
                                    data-testid="que-significa-input"
                                />
                                {step === 2 && (
                                    <Button
                                        onClick={() => canProceed(2) && setStep(3)}
                                        disabled={!canProceed(2)}
                                        className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white"
                                    >
                                        Continuar <ChevronDown className="h-4 w-4 ml-1" />
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Step 3 */}
                        {step >= 3 && (
                            <div className={`space-y-4 mb-8 pl-8 border-l-2 border-[#DCE3E8] ${step > 3 ? 'opacity-60' : ''}`}>
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        step > 3 ? 'bg-[#5FAF9D] text-white' : 'bg-[#3E5F6E] text-white'
                                    }`}>
                                        {step > 3 ? <Check className="h-4 w-4" /> : '3'}
                                    </div>
                                    <Label className="text-sm font-semibold text-[#3E5F6E]">
                                        ¿Qué es lo que más te duele de eso?
                                    </Label>
                                </div>
                                <Textarea
                                    value={queDueleMas}
                                    onChange={(e) => setQueDueleMas(e.target.value)}
                                    placeholder="Conecta con la emoción. ¿Cuál es el núcleo del dolor?"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    disabled={step > 3}
                                    data-testid="que-duele-input"
                                />
                                {step === 3 && (
                                    <Button
                                        onClick={() => canProceed(3) && setStep(4)}
                                        disabled={!canProceed(3)}
                                        className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white"
                                    >
                                        Continuar <ChevronDown className="h-4 w-4 ml-1" />
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Step 4 - Category Selection */}
                        {step >= 4 && (
                            <div className="space-y-4 pl-12 border-l-2 border-[#5FAF9D]">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-[#5FAF9D] flex items-center justify-center text-sm font-medium text-white">
                                        4
                                    </div>
                                    <Label className="text-sm font-semibold text-[#5FAF9D]">
                                        ¿Cuál es la categoría de tu temor raíz?
                                    </Label>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {CATEGORIAS_TEMOR.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setCategoriaFinal(cat.id)}
                                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                                                categoriaFinal === cat.id
                                                    ? `border-current ${cat.color} bg-opacity-20 shadow-md`
                                                    : 'border-[#DCE3E8] hover:border-[#3E5F6E]/50'
                                            }`}
                                            data-testid={`categoria-${cat.id}`}
                                        >
                                            <div className={`w-4 h-4 ${cat.color} rounded-full mx-auto mb-2`}></div>
                                            <span className={`font-medium text-sm ${
                                                categoriaFinal === cat.id ? cat.color.replace('bg-', 'text-') : 'text-[#1F2A30]'
                                            }`}>
                                                {cat.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div className="flex justify-end pt-8 border-t border-[#DCE3E8] mt-8">
                                <Button
                                    onClick={handleSubmit}
                                    disabled={readOnly || !categoriaFinal || loading}
                                    className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                                    data-testid="submit-arbol"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            Completar módulo
                                            <Check className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
